import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Container, Form, Button, Image, Row, Col, Card, Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setPlantInfo } from '../../store';
import config from '../../config';

const LogoManager = () => {
  const dispatch = useDispatch();
  const plantInfo = useSelector((state) => state.plantInfo);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewLogo, setPreviewLogo] = useState(null);
  const [logoList, setLogoList] = useState([]);
  const [imageLoadErrors, setImageLoadErrors] = useState({});
  const [selectedLogo, setSelectedLogo] = useState('');
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    setSelectedLogo(plantInfo.logo || '');
  }, [plantInfo.logo]);

  const fetchLogoList = async () => {
    try {
      const response = await axios.get(`${config.backendUrl}/logo_list`);
      setLogoList(response.data.logos);
    } catch (error) {
      console.error('로고 목록을 가져오는 중 오류 발생:', error);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setSelectedFile(file);
    setPreviewLogo(URL.createObjectURL(file));
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      alert('파일을 선택해주세요.');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await axios.post(`${config.backendUrl}/upload_logo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      alert('로고가 성공적으로 업로드되었습니다.');
      setSelectedFile(null);
      setPreviewLogo(null);
      fetchLogoList();
    } catch (error) {
      console.error('로고 업로드 중 오류 발생:', error);
      alert('로고 업로드에 실패했습니다.');
    }
  };

  const handleDelete = async (filename) => {
    if (filename === 'mrt.png') {
      alert('기본 로고는 삭제할 수 없습니다.');
      return;
    }
    try {
      await axios.delete(`${config.backendUrl}/delete_logo/${filename}`);
      alert(`${filename} 삭제 완료`);
      fetchLogoList();
      if (selectedLogo === filename) {
        setSelectedLogo('');
      }
    } catch (error) {
      console.error('로고 삭제 중 오류 발생:', error);
      alert('로고 삭제에 실패했습니다.');
    }
  };

  const handleSelect = (filename) => {
    setSelectedLogo(filename);
  };

  const handleApplyLogo = () => {
    axios.post(`${config.backendUrl}/api/update-info`, {
      name: plantInfo.name,
      address: plantInfo.address,
      callnumber: plantInfo.callnumber,
      logo: selectedLogo
    })
      .then(response => {
        dispatch(setPlantInfo({ ...plantInfo, logo: selectedLogo }));
        alert('로고가 성공적으로 적용되었습니다.');
        setShowModal(false);
      })
      .catch(error => {
        console.error('로고 적용 중 오류 발생:', error);
        alert('로고 적용에 실패했습니다.');
      });
  };

  const handleImageError = (logo) => {
    setImageLoadErrors(prev => ({...prev, [logo]: true}));
  };

  const handleShowModal = () => {
    setShowModal(true);
    fetchLogoList();
  };

  return (
    <Container>
      <div style={{ display: 'inline-block', padding: '10px' }}>
        <Image 
          src={`${config.backendUrl}/logo/${plantInfo.logo}`} 
          alt="현재 로고" 
          thumbnail 
          style={{ maxWidth: '200px', backgroundColor: '#3E90CC' }} 
          onError={() => {
            console.error(`현재 로고 로드 실패: ${config.backendUrl}/logo/${plantInfo.logo}`);
          }}
        />
      </div>
      <Button onClick={handleShowModal} style={{ marginLeft: '10px' }} className="mt-2">
        로고 변경하기
      </Button>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>로고 관리</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group controlId="logoUpload" className="mb-3">
            <Form.Label>새 로고 업로드</Form.Label>
            <Form.Control type="file" onChange={handleFileChange} accept="image/*" />
          </Form.Group>
          
          {previewLogo && (
            <Image src={previewLogo} alt="로고 미리보기" thumbnail className="mb-3" style={{ maxWidth: '200px' }} />
          )}
          
          <Button onClick={handleUpload} disabled={!selectedFile} className="mb-4">
            업로드하기 (최적사이즈 166x55)
          </Button>

          <h4>로고 목록</h4>
          <Row xs={2} md={3} lg={4} className="g-2">
            {logoList.map((logo) => (
              <Col key={logo}>
                <Card style={{ width: '100%', height: '100%' }}>
                  <div style={{ width: '120px', height: '60px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#3E90CC' }}>
                    {!imageLoadErrors[logo] ? (
                      <Card.Img 
                        variant="top" 
                        src={`${config.backendUrl}/logo/${logo}`} 
                        alt={logo} 
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        onError={() => handleImageError(logo)}
                      />
                    ) : (
                      <div className="text-center p-2">
                        <p className="text-danger" style={{ fontSize: '0.8em' }}>이미지 로드 실패</p>
                      </div>
                    )}
                  </div>
                  <Card.Body className="p-2">
                    <Card.Title style={{ fontSize: '0.8em', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{logo}</Card.Title>
                    <Button variant="primary" onClick={() => handleSelect(logo)} className="me-1 btn-sm" size="sm">
                      선택
                    </Button>
                    <Button 
                      variant="danger" 
                      onClick={() => handleDelete(logo)} 
                      className="btn-sm" 
                      size="sm"
                      disabled={logo === 'mrt.png'}
                    >
                      삭제
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          <h4 className="mt-4">현재 적용된 로고</h4>
          <div style={{ backgroundColor: '#3E90CC', display: 'inline-block', padding: '10px' }}>
            <Image 
              src={`${config.backendUrl}/logo/${selectedLogo || plantInfo.logo || 'mrt.png'}`} 
              alt="현재 적용된 로고" 
              thumbnail 
              style={{ maxWidth: '200px', backgroundColor: '#3E90CC' }} 
              onError={() => {
                console.error(`현재 적용된 로고 로드 실패: ${config.backendUrl}/logo/${selectedLogo || plantInfo.logo || 'mrt.png'}`);
                setSelectedLogo('mrt.png');
              }}
            />
          </div>
          <p>{selectedLogo || plantInfo.logo || 'mrt.png'}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            닫기
          </Button>
          <Button 
            variant="primary" 
            onClick={handleApplyLogo} 
            disabled={!selectedLogo || selectedLogo === plantInfo.logo}
          >
            로고 적용
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default LogoManager;